import React, { useState, useEffect, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { Col, Row, DatePicker, message } from "antd";
import api from "utils/api";
import utils from "utils";
import Loader from "Components/Loader";
import icons from "Common/Icons";
import dayjs from 'dayjs';
import IndividualVitalChart from "./IndividualVitalChart"; // Import the new component
const { RangePicker } = DatePicker;

const vitalDisplayChart = [
  "systolic_blood_pressure",
  "diastolic_blood_pressure",
  "pulse_rate",
  "temperature",
  "spo2",
  "respiratory_rate",
  "blood_glucose",
  "weight",
];

const VitalChart = (props) => {
  const [chartData, setChartData] = useState({
    seriesData: [],
    disableDateString: { vital_start_date: "", vital_end_date: "" },
  });
  const [load, setLoader] = useState(true);
  const labelUnit = useRef({});
  const [range, setRange] = useState([]);
  const rangeDateString = useRef([]);

  const fetchData = async () => {
    const patient_id = props?.patientDetails?.patient_id || "";
    const rangefinal = rangeDateString.current;
    try {
      const response = await api.fetch_vitals_chart(patient_id, rangefinal);
      if (response.data?.status === 200) {
        if (response.data?.data.count > 0) {
          const data = response.data.data.results.filter((item) => {
            return item.required_vitals.some((vital) => {
              return (
                vitalDisplayChart.includes(vital.field_value) &&
                vital.value !== ""
              );
            });
          });
          if (data.length > 0) {
            data[0].required_vitals.forEach((vital) => {
              labelUnit.current[vital.label] = vital.unit;
            });

            const vitalsMap = new Map();
            data.forEach((item) => {
              item.required_vitals.forEach((vital) => {
                if (
                  vitalDisplayChart.includes(vital.field_value) &&
                  !vitalsMap.has(vital.label)
                ) {
                  vitalsMap.set(vital.label, {
                    name: vital.label,
                    type:
                      vital.field_value === "systolic_blood_pressure" ||
                      vital.field_value === "diastolic_blood_pressure"
                        ? "line"
                        : "line",
                    data: [],
                    xAxisData: [],
                  });
                }
              });
            });

            data.forEach((item, index) => {
              item.required_vitals.forEach((vital) => {
                if (vitalsMap.has(vital.label)) {
                  const series = vitalsMap.get(vital.label);
                  if (vital.value) {
                    series.data.push(parseFloat(vital.value));
                    series.xAxisData.push(item.updated_on);
                  }
                }
              });
            });

            const seriesData = Array.from(vitalsMap.values()).filter((item) => {
              return (
                !item.data.every((value) => value === null) ||
                item.data.length > 0
              );
            });

            // Combine systolic and diastolic blood pressure into a single series
            const systolicSeries = seriesData.find(
              (series) => series.name === "Systolic Blood Pressure"
            );
            const diastolicSeries = seriesData.find(
              (series) => series.name === "Diastolic Blood Pressure"
            );
            const combinedSeriesData = seriesData.filter(
              (series) =>
                series.name !== "Systolic Blood Pressure" &&
                series.name !== "Diastolic Blood Pressure"
            );

            if (systolicSeries && diastolicSeries) {
              combinedSeriesData.push({
                name: "Blood Pressure",
                type: "line",
                data: systolicSeries.data.map((value, index) => ({
                  value: [
                    value,
                    diastolicSeries.data[index] || null,
                  ],
                })),
                xAxisData: systolicSeries.xAxisData,
                // encode: { y: [1, 2] },
                // dimensions: ["index", "Systolic", "Diastolic"],
              });
            }

            setChartData({
              seriesData: combinedSeriesData,
              disableDateString: {
                vital_start_date:
                  response.data.data.results[0]?.vital_start_date || "",
                vital_end_date:
                  response.data.data.results[0]?.vital_end_date || "",
              },
            });
          }
        } else {
          setChartData({
            seriesData: [],
            disableDateString: { vital_start_date: "", vital_end_date: "" },
          });
        }
        setLoader(false);
      } else {
        setLoader(false);
        message.error(response.data?.message);
      }
    } catch (err) {
      setLoader(false);
      message.error("Something went wrong");
    }
  };

  useEffect(() => {
    fetchData();
  }, [range]);

  const disabledDate = (current) => {
    const endDate = dayjs().endOf("day");
    return current && current.isAfter(endDate);
  };

  return (
    <>
      {load ? (
        <Loader />
      ) : (
        <div>
          <Row justify="space-between">
            <Col>
              <span style={{ fontSize: "16px", fontWeight: "bold" }}>
                Vitals Signs Over a Period of Time
              </span>
            </Col>
            <Col>
              <RangePicker
                value={range}
                onChange={(dates, dateStrings) => {
                  setRange(dates);
                  rangeDateString.current = dateStrings;
                }}
                disabledDate={disabledDate}
              />
            </Col>
          </Row>
          {chartData.seriesData.length > 0 ? (
            <div
              style={{
                height: "50vh",
                overflow: "auto",
                marginTop: "10px",
                paddingTop: "10px",
              }}
            >
              {chartData.seriesData.map((series, index) => (
                <div key={series.name}>
                  {/* <div className="chart-title">{series.name}</div> */}
                  <IndividualVitalChart
                    seriesData={[series]}
                    xAxisData={series.xAxisData}
                    labelUnit={labelUnit.current}
                    chartIndex={index}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="no-data">
              No data available for the selected range.
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default VitalChart;
